'use client'

import { AnimatePresence, motion } from 'framer-motion'
import { MinusCircle, PlusCircle } from 'lucide-react'
import Image from 'next/image'
import { useState } from 'react'
import type { GetProductOutput } from '@/http/zamply/products/get-product-schema'

interface ContentProps {
  product: GetProductOutput
}

export default function Content({ product }: ContentProps) {
  const [expandList, setExpandList] = useState(false)

  function handleExpandList() {
    setExpandList(state => !state)
  }

  const getContentDescriptionArray = (
    contentDescription: string | null | string[]
  ): string[] => {
    if (Array.isArray(contentDescription)) {
      return contentDescription
    }
    if (typeof contentDescription === 'string') {
      try {
        const parsed = JSON.parse(contentDescription)
        return Array.isArray(parsed) ? parsed : []
      } catch {
        return []
      }
    }
    return []
  }

  const productDescriptionParse = getContentDescriptionArray(
    product.content_description
  )

  const basicTopics: string[] = []
  const expandedContent: string[] = []

  productDescriptionParse.forEach((topics: string, index: number) => {
    if (index < 5) {
      basicTopics.push(topics)
    } else {
      expandedContent.push(topics)
    }
  })

  return (
    <div className="mt-6 md:mt-9" id="list-init">
      <p className="font-medium text-[#877E94] text-sm md:text-base">
        {product.description}
      </p>
      <div className="flex w-full justify-center">
        <Image
          alt=""
          className="mt-8 max-h-100 w-full max-w-200 rounded-lg object-cover"
          height={23.25}
          src={product.image_url ?? ''}
          width={37}
        />
      </div>
      <div className="mt-8">
        <h4 className="font-bold text-lg text-primary-700">
          CONTEÚDO PROGRAMÁTICO
        </h4>

        <div className="mt-4 space-y-3">
          <ul className="space-y-3">
            {basicTopics.map((topic, index) => (
              <li className="flex items-start gap-3" key={index}>
                <p className="font-medium text-[#877E94] text-sm md:text-base">
                  - {topic}
                </p>
              </li>
            ))}
          </ul>

          <AnimatePresence initial={false}>
            {expandList && (
              <motion.ul
                animate={{ height: 'auto', opacity: 1 }}
                className="space-y-3"
                exit={{ height: 0, opacity: 0 }}
                initial={{ height: 0, opacity: 0 }}
                style={{ overflow: 'hidden' }}
                transition={{ duration: 0.4, ease: 'easeInOut' }}
              >
                {expandedContent.map((topic, index) => (
                  <motion.li
                    animate={{ y: 0, opacity: 1 }}
                    className="flex items-start"
                    initial={{ y: 20, opacity: 0 }}
                    key={index}
                    transition={{ delay: index * 0.1, duration: 0.3 }}
                  >
                    <p className="font-medium text-[#877E94] text-sm md:text-base">
                      - {topic}
                    </p>
                  </motion.li>
                ))}
              </motion.ul>
            )}
          </AnimatePresence>
        </div>

        <button
          className="mt-4 flex items-center gap-0.5 font-bold text-primary-700 text-sm"
          onClick={handleExpandList}
          type="button"
        >
          <motion.div
            animate={{ rotate: expandList ? 180 : 0 }}
            transition={{ duration: 0.2 }}
          >
            {expandList ? (
              <MinusCircle className="h-4 w-4 fill-primary-700 text-white" />
            ) : (
              <PlusCircle className="h-4 w-4 fill-primary-700 text-white" />
            )}
          </motion.div>

          {expandList ? 'Ver menos' : 'Ver conteúdo completo'}
        </button>
      </div>
    </div>
  )
}
